'use client';

import { createContext, useContext } from 'react';

/**
 * Framework-free router seam (rule 07). Tenant client components consume
 * `useAppRouter()` / `useAppPathname()` / `useAppSearchParams()` instead of
 * importing `next/navigation` directly. The Next-specific implementation lives
 * shell-side in `src/next/AppRouterProvider.tsx`, which is the ONLY client
 * module allowed to import `next/navigation`.
 *
 * ## The 1:1 constraint — hook FUNCTIONS through context, invoked per call site
 *
 * `useSearchParams()` (and, in some Next versions, `usePathname()`) triggers
 * Next's dynamic-render / CSR bailout. If the provider called those hooks
 * itself, it would opt the WHOLE subtree out of static/streaming SSR. So the
 * context carries the host's hook *functions* and each consumer hook invokes
 * them at its own call site — semantically identical to a direct
 * `useSearchParams()` import there, preserving Next's per-call-site bailout
 * scope exactly. The provider must NEVER call `usePathname` / `useSearchParams`.
 */

export type AppNavigate = {
  readonly push: (href: string) => void;
  readonly replace: (href: string) => void;
  readonly refresh: () => void;
  readonly back: () => void;
  readonly prefetch: (href: string) => void;
};

/**
 * Read-only search-params surface. A structural subset of the web
 * `URLSearchParams` so the seam stays framework-free: both the standard
 * `URLSearchParams` and Next's `ReadonlyURLSearchParams` satisfy it, without
 * importing any `next/*` type into shared engine code.
 */
export type AppSearchParams = Pick<
  URLSearchParams,
  'get' | 'getAll' | 'has' | 'keys' | 'values' | 'entries' | 'forEach' | 'toString'
>;

export type AppRouterContextValue = {
  readonly navigate: AppNavigate;
  // Hook FUNCTIONS — invoked at each consumer call site to preserve Next's
  // per-call-site Suspense/CSR-bailout semantics EXACTLY. Never called in the
  // provider.
  readonly usePathname: () => string;
  readonly useSearchParams: () => AppSearchParams;
};

const AppRouterContext = createContext<AppRouterContextValue | null>(null);

export const AppRouterContextProvider = AppRouterContext.Provider;

const useAppRouterContext = (): AppRouterContextValue => {
  const ctx = useContext(AppRouterContext);
  if (ctx === null) {
    throw new Error('useAppRouter* must be used within <AppRouterProvider>');
  }
  return ctx;
};

export const useAppRouter = (): AppNavigate => useAppRouterContext().navigate;

export const useAppPathname = (): string => {
  const { usePathname } = useAppRouterContext();
  return usePathname();
};

export const useAppSearchParams = (): AppSearchParams => {
  const { useSearchParams } = useAppRouterContext();
  return useSearchParams();
};
